import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';
import { Edit2 as EditIcon } from 'react-feather';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Constants } from '@trinity/chat-components/components/Theme';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
interface MessageSurvey {
  id: string;
  name: string;
  data: Record<string, unknown>;
  segmentIdentifier: string | null;
  createdOn: string;
  updatedOn: string;
}
import { SurveyTemplateSelector } from './SurveyTemplateSelector.js';
import { SurveyConfigForm } from './SurveyConfigForm.js';
import type { SurveyFormData, SurveyMessage } from './SurveyConfigForm.js';

type SurveyView =
  | { screen: 'list' }
  | { screen: 'template-select'; selectedMessages: SurveyMessage[] }
  | { screen: 'config'; selectedMessages: SurveyMessage[]; existingSurvey?: MessageSurvey }
  | { screen: 'add-to-survey'; messagesToAdd: SurveyMessage[] };

type Props = {
  segmentId?: string;
  segmentName?: string;
  onSurveyClick?: (survey: { id: string; name: string }) => void;
  pendingMessages?: SurveyMessage[] | null;
  onPendingConsumed?: () => void;
  pendingAddMessages?: SurveyMessage[] | null;
  onPendingAddConsumed?: () => void;
};

export const SurveySidePanel = ({ segmentId, onSurveyClick, pendingMessages, onPendingConsumed, pendingAddMessages, onPendingAddConsumed }: Props) => {
  const apiClient = useChatApiClient();
  const queryClient = useQueryClient();
  const [view, setView] = useState<SurveyView>({ screen: 'list' });

  useEffect(() => {
    if (pendingMessages && pendingMessages.length > 0) {
      setView({ screen: 'template-select', selectedMessages: pendingMessages });
      onPendingConsumed?.();
    }
  }, [pendingMessages, onPendingConsumed]);

  useEffect(() => {
    if (pendingAddMessages && pendingAddMessages.length > 0) {
      setView({ screen: 'add-to-survey', messagesToAdd: pendingAddMessages });
      onPendingAddConsumed?.();
    }
  }, [pendingAddMessages, onPendingAddConsumed]);

  const { data: surveys = [], isLoading } = useQuery({
    queryKey: ['message-surveys', { segmentId }],
    queryFn: async () => {
      if (!segmentId) return [];
      return apiClient.surveys.getSurveys(segmentId);
    },
    enabled: !!segmentId,
  });

  const handleSave = async (data: SurveyFormData, existingSurvey?: MessageSurvey) => {
    if (!segmentId) return;

    const payload = {
      name: 'Message Selection',
      data: {
        questions: data.questions,
        messages: data.messages.map((m) => ({ id: m.id, displayId: m.displayId ?? null, title: m.title, description: m.body, category: m.category })),
      },
      segmentIdentifier: segmentId,
    };

    if (existingSurvey) {
      await apiClient.surveys.updateSurvey(existingSurvey.id, payload);
    } else {
      await apiClient.surveys.createSurvey(payload);
    }

    await queryClient.invalidateQueries({ queryKey: ['message-surveys', { segmentId }] });
    setView({ screen: 'list' });
  };

  const computeDisplayId = (msg: { category?: string | null }, idx: number, list: Array<{ category?: string | null }>) => {
    const letter = msg.category?.trim().charAt(0).toUpperCase() || 'M';
    const count = list
      .slice(0, idx + 1)
      .filter((m) => (m.category?.trim().charAt(0).toUpperCase() || 'M') === letter).length;
    return `${letter}${count}`;
  };

  const handleAddToSurvey = async (survey: MessageSurvey, messagesToAdd: SurveyMessage[]) => {
    const surveyData = survey.data as { questions?: unknown[]; messages?: Array<{ id: number; displayId?: string; title: string; description?: string; category?: string | null }> } | undefined;
    const existingMessages = surveyData?.messages ?? [];
    const existingIds = new Set(existingMessages.map((m) => m.id));
    const newMessages = messagesToAdd.filter((m) => !existingIds.has(m.id));

    if (newMessages.length === 0) {
      setView({ screen: 'list' });
      return;
    }

    const allMessages = [
      ...existingMessages,
      ...newMessages.map((m) => ({ id: m.id, title: m.title, description: m.body, category: m.category })),
    ];

    const merged = allMessages.map((m, i, arr) => ({
      ...m,
      displayId: computeDisplayId(m, i, arr),
    }));

    await apiClient.surveys.updateSurvey(survey.id, {
      data: { ...surveyData, messages: merged },
    });

    await queryClient.invalidateQueries({ queryKey: ['message-surveys', { segmentId }] });
    setView({ screen: 'list' });
  };

  if (view.screen === 'add-to-survey') {
    return (
      <Root>
        <Content>
          <Header>
            <StyledAutoAwesomeIcon />
            <Title>Add to Survey</Title>
          </Header>
          <AddInfo>{view.messagesToAdd.length} message{view.messagesToAdd.length !== 1 ? 's' : ''} selected</AddInfo>
          <ScrollArea>
            {isLoading ? (
              <EmptyState>
                Loading surveys…
                <CircularProgress size={20} thickness={4} sx={{ color: '#999', marginTop: '8px' }} />
              </EmptyState>
            ) : surveys.length === 0 ? (
              <EmptyState>No surveys to add to. Create a survey first.</EmptyState>
            ) : (
              surveys.map((survey) => (
                <SurveyCard
                  key={survey.id}
                  onClick={() => handleAddToSurvey(survey, view.messagesToAdd)}
                >
                  <SurveyCardContent>
                    <SurveyName>{survey.name}</SurveyName>
                    <SurveyMeta>
                      {(survey.data as { messages?: unknown[] })?.messages?.length ?? 0} messages
                    </SurveyMeta>
                  </SurveyCardContent>
                </SurveyCard>
              ))
            )}
          </ScrollArea>
          <AddToSurveyFooter>
            <AddToSurveyCancelButton onClick={() => setView({ screen: 'list' })}>Cancel</AddToSurveyCancelButton>
          </AddToSurveyFooter>
        </Content>
      </Root>
    );
  }

  if (view.screen === 'template-select') {
    return (
      <SurveyTemplateSelector
        onTemplateSelect={() => {
          setView({ screen: 'config', selectedMessages: view.selectedMessages });
        }}
        onCancel={() => setView({ screen: 'list' })}
      />
    );
  }

  if (view.screen === 'config') {
    const existingData = view.existingSurvey?.data as { questions?: unknown[]; messages?: unknown[] } | undefined;
    return (
      <SurveyConfigForm
        initialMessages={view.selectedMessages}
        initialQuestions={existingData?.questions as never}
        onSave={(data) => handleSave(data, view.existingSurvey)}
        onCancel={() => setView({ screen: 'list' })}
      />
    );
  }

  return (
    <Root>
      <Content>
        <Header>
          <StyledAutoAwesomeIcon />
          <Title>Segment Surveys</Title>
          <CountBadge>{surveys.length}</CountBadge>
        </Header>
        <ScrollArea>
          {isLoading ? (
            <EmptyState>
              Loading surveys…
              <CircularProgress size={20} thickness={4} sx={{ color: '#999', marginTop: '8px' }} />
            </EmptyState>
          ) : surveys.length === 0 ? (
            <EmptyState>No surveys available for this segment</EmptyState>
          ) : (
            surveys.map((survey) => (
              <SurveyCard key={survey.id}>
                <SurveyCardContent onClick={() => onSurveyClick?.(survey)}>
                  <SurveyName>{survey.name}</SurveyName>
                  <SurveyMeta>
                    {(survey.data as { questions?: unknown[] })?.questions?.length ?? 0} questions · {(survey.data as { messages?: unknown[] })?.messages?.length ?? 0} messages
                  </SurveyMeta>
                </SurveyCardContent>
                <EditButton
                  onClick={() => {
                    const data = survey.data as { questions?: unknown[]; messages?: SurveyMessage[] } | undefined;
                    setView({
                      screen: 'config',
                      selectedMessages: data?.messages ?? [],
                      existingSurvey: survey,
                    });
                  }}
                  title="Edit survey"
                >
                  <EditIcon size={14} />
                </EditButton>
              </SurveyCard>
            ))
          )}
        </ScrollArea>
      </Content>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
  border-radius: ${Constants.Modal.BorderRadius}px;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.palette.gray.light2};
  position: relative;
`;

const Content = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--pri-01);
`;

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px 0;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
`;

const CountBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 2px 8px;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 5px 8px 8px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const SurveyCard = styled.div`
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #6366f1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SurveyCardContent = styled.div`
  flex: 1;
  padding: 12px;
  cursor: pointer;
`;

const SurveyName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`;

const SurveyMeta = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  margin-right: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #999;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f0f0f0;
    color: #6366f1;
  }
`;

const AddInfo = styled.div`
  font-size: 12px;
  color: #6366f1;
  font-weight: 600;
  padding: 0 8px 8px;
`;

const AddToSurveyFooter = styled.div`
  padding: 8px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
`;

const AddToSurveyCancelButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #d3d3d3;
  border-radius: 6px;
  background: transparent;
  color: #626262;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #999;
    background: #fafafa;
  }
`;
